import { EventType, Tracker, TrackerData, TrackOptions, BaseContent, AdvertisingIdentifiers } from './types';
import { uuidv4 } from './uuid';

const PULSE_WINDOW_KEY = 'pulse';

function pulse(...args: any): void {
    window[PULSE_WINDOW_KEY] =
        window[PULSE_WINDOW_KEY] ||
        function (...argz: any) {
            (window[PULSE_WINDOW_KEY].q = window[PULSE_WINDOW_KEY].q || []).push(argz);
        };

    window[PULSE_WINDOW_KEY].q = window[PULSE_WINDOW_KEY].q || [];
    window[PULSE_WINDOW_KEY].q.push(args);
}

/**
 * Checks if user consent has been set for any purpose in the TCF (Transparency and Consent Framework).
 *
 * This function examines the global `_tcf_` object to determine if consent decisions
 * (either 'accepted' or 'rejected') have been made for any purposes. It safely handles
 * cases where the TCF object or required methods may not be available.
 *
 * @returns {boolean} `true` if at least one purpose has a consent status of 'accepted'
 *                   or 'rejected', `false` if no consent is set, TCF is unavailable,
 *                   or an error occurs during the check
 *
 */
function waitForConsent(): boolean {
    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tcf = (window as any)._tcf_;

        if (!tcf || typeof tcf.getCachedOrDefaultConsentsForPulse !== 'function') {
            // TCF is not available, don't wait for consent
            return false;
        }

        const consents = tcf.getCachedOrDefaultConsentsForPulse();
        const purposes = consents?.purposes;

        if (!purposes) {
            // No purposes found, something is wrong, don't wait for consent
            console.log('finn-pulse-sdk: purposes not found in consent data.');
            return false;
        }

        // Check if any purpose has a definitive consent status
        const hasDefinedConsent = !Object.values(purposes).some(
            (purpose: any) => purpose.status === 'accepted' || purpose.status === 'rejected',
        );

        return hasDefinedConsent;
    } catch (e) {
        console.log('finn-pulse-sdk: exception during consent check.', e);
        // On error, don't wait for consent to avoid blocking tracking
        return false;
    }
}

/**
 * Executes a callback function when user consent is set or when the page is unloading.
 * @param trackFn - The callback function to execute when consent is set
 * @see {@link https://github.schibsted.io/UFo-User-Choices/cmp-sdk?tab=readme-ov-file#isconsentedtoall}
 */
function runWhenConsentEstablished(trackFn: () => void): void {
    // This uses the TCF (Transparency and Consent Framework) SDK to check
    // it triggers when the user has consented to all purposes. The provided callback will be
    // executed when consent information is available.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any)._tcf_?.isConsentedToAll(() => {
        trackFn();
    });

    // In case of unloaded, we call the function
    window.addEventListener('beforeunload', () => {
        // If we are still waiting for consent, we call the track function
        // because the user is leaving the page and we won't get a chance later.
        if (waitForConsent() === true) {
            trackFn();
        }
    });
}

function doTrackEvent(data: TrackerData, trackOptions?: TrackOptions): void {
    addVerticalType(data);

    // Remove this call when the missing .id bug on Job object is fixed in pulse-sdk
    handleJobObjectQuirk(data);

    if (shouldAddPageContextToObject(data) && !trackOptions?.disableAddPageContext) {
        pulse('pageStateTracker', (pageStateTracker: Tracker) => {
            pageStateTracker.evaluateEventInputs().then((trackerState: TrackerData) => {
                if (data.object) {
                    // Attach '.object.page' from pageStateTracker.
                    data.object.page = {
                        ...trackerState.object,
                    };

                    // Add the ClassifiedAd object from the underlying
                    // page view to '.object.items' if the pageStateTracker
                    // has tracked a ClassifiedAd view.
                    //
                    // Merge it with the existing object in '.object.items'
                    // if one already exists.

                    if (trackerState.object?.type === 'ClassifiedAd' && !trackOptions?.disableAddClassifiedAdToItems) {
                        if (data.object?.items) {
                            const classifiedAdIndex = data.object.items.findIndex(
                                (item: BaseContent) => item.type === 'ClassifiedAd',
                            );
                            if (classifiedAdIndex > -1) {
                                data.object.items[classifiedAdIndex] = {
                                    ...trackerState.object,
                                    ...data.object.items[classifiedAdIndex],
                                };
                            } else {
                                data.object.items.push(trackerState.object);
                            }
                        } else {
                            data.object.items = [trackerState.object];
                        }
                    }
                }

                // Attach '.vertical' from pageStateTracker if not set.
                // It may have been set using 'setState()' for the default tracker,
                // but we set it here so we also can attach it automatically if
                // only trackPage() with vertical information has been used.
                if (!data.vertical?.name && trackerState.vertical?.name) {
                    data.vertical = trackerState.vertical;
                }

                data.pageViewId = trackerState.pageViewId;

                track('trackerEvent', data, trackOptions);
            });
        });
    } else {
        track('trackerEvent', data, trackOptions);
    }
}

export function trackEvent(data: TrackerData, trackOptions?: TrackOptions): void {
    if (waitForConsent() === true) {
        runWhenConsentEstablished(() => {
            doTrackEvent(data, trackOptions);
        });
    } else {
        doTrackEvent(data, trackOptions);
    }
}

function doTrackView(data: TrackerData, trackOptions?: TrackOptions): void {
    addVerticalType(data);

    // We need to explicitly generate the pageViewId so we can set it for both tracker instances,
    // and manually call 'track()' with '@type = View', to avoid the pageViewId being overwritten.
    //
    // Note: We'll need to wait for this commit
    // https://github.schibsted.io/spt-dataanalytics/pulse-event-builders/commit/38fd7e5484440eb227aea180160a2efb839ba11d
    // to be propagated to the pulse-autotracker transitively via the pulse-js-sdk
    // before it will work to explicitly provide a pageViewId.
    //
    data.pageViewId = uuidv4();
    data.type = 'View';

    // Set pageViewId for event tracker and store page state in page tracker.
    pulse('update', { pageViewId: data.pageViewId }, true);
    pulse('pageStateTracker.update', data, false);

    // cmp-sdk does not update consent state for pageStateTracker automatically,
    // so we need to set it manually here.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const consents = (window as any)._tcf_?.getCachedOrDefaultConsentsForPulse?.();
    if (consents) {
        pulse('pageStateTracker.setConsents', consents);
    }

    // Track page view using page tracker.
    pulse('pageStateTracker.track', 'trackerEvent', data, trackOptions);
}

/**
 * For apps that do client-side routing we get multiple calls to trackPageView
 * without a full page reload. Our bfcache handling in trackPageView needs some
 * extra work in this case to ensure we only call the last registered trackPageView,
 * since that is the page that got restored.
 */
const bfcacheListeners: Array<(e: PageTransitionEvent) => void> = [];

/**
 * Tracks a page view event with the provided data.
 *
 * This function:
 * 1. Records the initial page view
 * 2. Sets up a listener for the 'pageshow' event to detect when a page is
 *    restored from the browser's back-forward cache (bfcache)
 * 3. Re-tracks the page view only when restored from cache (event.persisted=true)
 *    to ensure accurate tracking of user navigation
 *
 * The bfcache tracking is important because browsers may serve a cached version
 * of the page when users navigate back/forward, which would otherwise not trigger
 * normal page load tracking.
 *
 * @param data Optional tracking data to include with the page view
 * @param trackOptions Optional tracking configuration options
 */
export function trackPageView(data: TrackerData = {}, trackOptions?: TrackOptions): void {
    if (waitForConsent() === true) {
        runWhenConsentEstablished(() => {
            doTrackView(data, trackOptions);
        });
    } else {
        doTrackView(data, trackOptions);
    }

    // Register a listener on pageshow for if the page is restored from bfcache
    const bfcacheListener = (event: PageTransitionEvent) => {
        if (event.persisted) {
            doTrackView(data, trackOptions);
        }
    };
    window.addEventListener('pageshow', bfcacheListener);

    // If there are existing bfcache listeners, unregister them to avoid
    // firing multiple page view events on a cache restore for client-side
    // routed apps.
    for (const listener of bfcacheListeners) {
        window.removeEventListener('pageshow', listener);
    }

    // List the current one as the one true bfcache listener
    bfcacheListeners.push(bfcacheListener);
}

export function setState(state: TrackerData): void {
    addVerticalType(state);
    pulse('update', state, true);
    pulse('pageStateTracker.update', state, true);
}

export function setPageState(pageState: TrackerData): void {
    addVerticalType(pageState);
    pulse('pageStateTracker.update', pageState, true);
}

export function getPageState(callback: (pageState: TrackerData) => void): void {
    pulse('pageStateTracker', (pageStateTracker: Tracker) => {
        pageStateTracker.evaluateEventInputs().then((trackerState: TrackerData) => {
            callback(trackerState);
        });
    });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getTracker(callback: (trackerRef: any) => void): void {
    pulse(callback);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getPageStateTracker(callback: (trackerRef: any) => void): void {
    pulse('pageStateTracker', callback);
}

export function trackExperiment(name: string, variant: string): void {
    if (name && variant) {
        const experimentId = `${name}_${variant}`;
        const data: TrackerData = {
            type: 'View',
            intent: 'Load',
            name: 'Experiment exposure',
            object: {
                id: 'ex_ev16',
                type: 'UIElement',
                elementType: 'Experiment',
            },
            experiments: [
                {
                    platform: 'finn',
                    id: `sdrn:finn:experiment:${experimentId}`,
                },
            ],
        };
        track('trackerEvent', data);
    }
}

export function getAdvertisingIdentifiers(): Promise<AdvertisingIdentifiers> {
    return new Promise((resolve, reject) => {
        pulse('pageStateTracker', (pageStateTracker: Tracker) => {
            pageStateTracker
                .getAdvertisingIdentifiers(['xandr', 'delta', 'adform'])
                .then((ids: AdvertisingIdentifiers) => resolve(ids))
                .catch((error) => reject(error));
        });
    });
}

function track(eventType: EventType, data: TrackerData = {}, trackOptions?: TrackOptions): void {
    pulse('track', eventType, data, trackOptions);
}

function addVerticalType(data: TrackerData): void {
    // Setting this statically in the initializer does not work,
    // since every call may have '.vertical' as input, and will overwrite
    // '.vertical' stored in the tracker state, since we don't
    // have any event builder for this (FINN specific) object.
    if (data.vertical) {
        data.vertical['@type'] = 'MarketplaceVertical';
    }
}

function handleJobObjectQuirk(data: TrackerData): void {
    // 'id' is not defined for the 'Job' object, but due to a quirk in the
    // pulse-event-builders library used by the pulse-js-dk, we must
    // provide an 'id' so that the 'type' property is preserved.
    // See https://sch-chat.slack.com/archives/C03NNRFC1/p1622021084042500
    if (data.object?.items) {
        for (const x of data.object?.items) {
            if (x.type === 'ClassifiedAd') {
                if (x.items) {
                    for (const y of x.items) {
                        if (y.type === 'Job') {
                            y.id = 'job';
                        }
                    }
                }
            }
        }
    }
}

function shouldAddPageContextToObject(data: TrackerData): boolean {
    return (
        data.object?.type === 'UIElement' ||
        data.object?.type === 'RecommendationList' ||
        data.object?.type === 'ListingItem' ||
        data.object?.type === 'RecommendationItem'
    );
}

export default {
    trackEvent,
    trackPageView,
    trackExperiment,
    setState,
    setPageState,
    getPageState,
    getTracker,
    getPageStateTracker,
    getAdvertisingIdentifiers,
};

export * from './types';
